import { $createParagraphNode, $getRoot, CLEAR_HISTORY_COMMAND, type LexicalEditor } from "lexical";
import { $assignFreshBlockId } from "../features/block-id/extension";
import type { ComposerMessage } from "../features/composer/submit";
import { createDocument, type AttachmentRef, type DocumentId, type EditorDocument } from "../index";
import { engineVersion } from "./handle";

export {
  createComposerSubmit,
  createPendingStore,
  isBlankMessage,
  type ComposerMessage,
  type ComposerSubmission,
  type ComposerSubmitOptions,
  type PendingStore,
  type SubmitOutcome,
} from "../features/composer/submit";

/**
 * The draft as it is now. The text is read at once, and `document` is built when it is first read:
 * the empty-message policy is asked every time the text changes, and the usual one looks at the
 * text alone. An editor state does not change once it is committed, so reading it late is safe.
 */
export function readComposerMessage(
  editor: LexicalEditor,
  documentId: DocumentId,
  attachments: readonly AttachmentRef[] = [],
): ComposerMessage {
  const state = editor.getEditorState();
  let document: EditorDocument | null = null;
  return {
    attachments,
    plainText: state.read(() => $getRoot().getTextContent()),
    get document() {
      return (document ??= createDocument(documentId, state.toJSON(), engineVersion(editor)));
    },
  };
}

/** Empties the editor for the next draft: `clearDraft` of `createComposerSubmit`, on Lexical. */
export function clearDraft(editor: LexicalEditor): void {
  editor.update(
    () => {
      const paragraph = $createParagraphNode();
      $getRoot().clear().append(paragraph);
      // A new draft, not the sent message continued: its first block gets an ID of its own.
      $assignFreshBlockId(paragraph);
      paragraph.select();
    },
    {
      discrete: true,
      // Enter reaches this from inside an update, where the clearing is queued rather than run.
      // Dropping history once the clearing has landed is right whichever way this was reached.
      onUpdate: () => editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined),
    },
  );
}
